/**
 * TOSS Web Application - Axios API Client
 *
 * Centralized HTTP client for all API requests.
 * Configured with:
 * - Base URL from environment variables
 * - Default headers (JSON content type)
 * - Request/response interceptors for JWT handling
 * - Automatic token refresh on 401 responses
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import {
  setupRequestInterceptor,
  setupResponseInterceptor,
  TokenStorage,
} from './interceptors';
import {
  ERROR_MESSAGES,
  ERROR_REASON_MESSAGES,
  HTTP_ERROR_MESSAGES,
  SYSTEM_ERROR_MESSAGES,
  DEFAULT_ERROR_MESSAGE,
  isRussianMessage,
} from '@/constants';

// ============================================================================
// Configuration
// ============================================================================

/**
 * API base URL from environment or default to relative path (for Vite proxy)
 */
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Build full URL for file downloads/views.
 *
 * **The server signs; this function does not.** Since ADR-040 the three legacy download
 * routes (`/api/files/{id}`, `/api/lk/files/{id}`, `/api/mobile/files/{id}`) are guarded
 * by `[RequireFileSignature]`, and the signature is minted server-side when the DTO is
 * built. Every URL that reaches this function through a DTO field already carries
 * `?exp=…&sig=…`; the database still stores the bare `/api/files/{id}`. So the job here is
 * narrow: turn a relative path into an absolute one **without losing the query string**,
 * and keep appending the JWT for the one case the signature does not cover.
 *
 * (The previous version of this docstring said signing was forbidden by design and cited
 * decision Р10. Р10 was overridden by Р-2: it now applies only to `/api/client/files/*`.)
 *
 * **Why `?access_token=` is still appended, and only to `/api/lk/files/*`.** It is the
 * second door of the gate: an authenticated INTERNAL or MOBILE caller is let through
 * without a signature. That matters for a URL the user constructs rather than receives —
 * a hand-edited address, an old bookmark, a link pasted into a chat — and for the window
 * between a signature expiring and the DTO being refetched. Browser-native `<img>`,
 * `<video>` and `<a download>` requests cannot attach an `Authorization` header at all,
 * which is why it goes in the query string, exactly as SignalR hubs already do. The
 * backend hook that parses it (`ServiceCollectionExtensions.Auth`) covers all three file
 * routes as of Р2; before that it matched only `/api/files` and silently discarded the
 * token this function had just added.
 *
 * **Deliberately NOT extended to `/api/client/files/*`.** Decision Р10 still holds there:
 * the client portal fetches with the normal `Authorization` header and renders `blob:`
 * URLs, specifically so a customer's JWT never sits in an address bar, browser history or
 * an nginx access log. A signature would be worse than the token, not better — it is a
 * tenant-blind bearer capability, and `ClientFileService.HasAccessAsync` (ownership) must
 * stay the only key to a customer's files. `/api/client/files/{id}` refuses signatures.
 *
 * For absolute URLs (https://...) or non-file paths we return the URL unchanged.
 *
 * When there is no access token yet (pre-login race during first render, other-tab
 * logout, or briefly during refresh), we return `undefined` for `/api/lk/files/*` paths
 * instead of an unsigned URL. Unsigned `<img src>` requests hit the backend, get
 * 401/404 and — worse — the PWA Workbox image cache can lock the failure into long-lived
 * cache entries. `src={undefined}` cleanly skips the request.
 *
 * @param path - Relative path like '/api/files/guid?exp=…&sig=…' or absolute URL
 * @returns Full URL, with `?access_token=...` added for `/api/lk/files/*`, or
 * `undefined` for such a path when no token is available.
 */
/** Hosts we own — if an absolute URL points here, it still needs the JWT. */
const OWN_FILE_HOSTS = ['apitoss.2-pcs.io', 'toss.2-pcs.io', 'lk.tosspto.com'];

export function getFileUrl(path: string | undefined | null): string | undefined {
  if (!path) return undefined;

  // An object URL or an inline image is already the whole address — it has no host to
  // resolve against and no route to sign. Prefixing it with `API_BASE_URL` produced
  // `/apiblob:http://…` in every build where that variable is set (docker-compose `/api`,
  // prod `https://api.tosspto.com`) and worked in dev only because there it is empty. The
  // portal hits this on its own files: `/api/client/files/*` is fetched with the
  // `Authorization` header and rendered from `URL.createObjectURL`, so what
  // `ClientAttachmentList` hands to `ImagePreviewModal` is a `blob:` URL, not a path.
  // An object URL or an inline image is already the whole address — it has no host to
  // resolve against and no route to sign. Prefixing it with `API_BASE_URL` produced
  // `/apiblob:http://…` in every build where that variable is set (docker-compose `/api`,
  // prod `https://api.tosspto.com`) and worked in dev only because there it is empty. The
  // portal hits this on its own files: `/api/client/files/*` is fetched with the
  // `Authorization` header and rendered from `URL.createObjectURL`, so what
  // `ClientAttachmentList` hands to `ImagePreviewModal` is a `blob:` URL, not a path.
  if (path.startsWith('blob:') || path.startsWith('data:')) {
    return path;
  }

  // Absolute URL: if it points to one of our hosts and `/api/lk/files/*`, treat
  // as relative so we still append `access_token`. Mobile clients sometimes
  // persist absolute URLs like `https://apitoss.2-pcs.io/api/lk/files/{id}`.
  // `parsed.search` is kept deliberately — dropping it would strip the server's
  // `?exp=&sig=` and turn a working link into a 404 (ADR-040). For any other
  // external URL, pass through unchanged.
  if (path.startsWith('http://') || path.startsWith('https://')) {
    try {
      const parsed = new URL(path);
      const sameOrigin =
        parsed.origin === window.location.origin ||
        OWN_FILE_HOSTS.some((h) => parsed.host === h || parsed.host.endsWith(`.${h}`));
      if (sameOrigin && parsed.pathname.startsWith('/api/lk/files/')) {
        path = parsed.pathname + parsed.search;
      } else {
        return path;
      }
    } catch {
      return path;
    }
  }

  const fullUrl = `${API_BASE_URL}${path}`;

  // Only add the JWT to `/api/lk/files/*`. Everything else is returned as it came,
  // signature and all: a server-signed `/api/files/{id}?exp=&sig=` needs nothing from
  // us, static assets need nothing, and `/api/client/files/*` must never carry our
  // JWT (see this function's docstring).
  if (!path.startsWith('/api/lk/files/')) {
    return fullUrl;
  }

  const token = TokenStorage.getAccessToken();
  if (!token) return undefined;

  const separator = fullUrl.includes('?') ? '&' : '?';
  return `${fullUrl}${separator}access_token=${encodeURIComponent(token)}`;
}

/**
 * Request timeout in milliseconds (30 seconds)
 */
const REQUEST_TIMEOUT = 30000;

// ============================================================================
// Axios Instance
// ============================================================================

/**
 * Main Axios instance for all API requests
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  paramsSerializer: {
    serialize: (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, String(v)));
        } else {
          searchParams.append(key, String(value));
        }
      });
      return searchParams.toString();
    },
  },
});

// ============================================================================
// Interceptors Setup
// ============================================================================

// Setup request interceptor for adding JWT token
setupRequestInterceptor(apiClient);

// Setup response interceptor for handling errors and token refresh
setupResponseInterceptor(apiClient);

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if the error is an Axios error
 */
export function isAxiosError<T = unknown>(error: unknown): error is AxiosError<T> {
  return axios.isAxiosError(error);
}

/**
 * Backend error codes whose server-side Russian message is more precise than our
 * mapping in ERROR_MESSAGES, and therefore wins over it. Deliberately narrow —
 * for every other code the curated frontend text is the better one.
 */
// DUPLICATE_ENTITY: every existing backend usage (Stations, Terminals, EmailSenderRules)
// already names the specific conflict («Станция «…» уже существует», «„mail.ru" уже
// закреплён за заказчиком «…»») — strictly more useful than the generic frontend text.
// EMAIL_GATEWAY_INVALID_STATE / EMAIL_GATEWAY_RAW_UNAVAILABLE: new codes (client portal
// Р6, email gateway), used nowhere else — zero blast radius. The 409 text names the
// letter's REAL current state («Текущее состояние письма: «Сопоставлено»»), which no
// static frontend string can reproduce.
const PREFER_BACKEND_MESSAGE_CODES = new Set([
  'ENTITY_IN_USE',
  'DUPLICATE_ENTITY',
  'EMAIL_GATEWAY_INVALID_STATE',
  'EMAIL_GATEWAY_RAW_UNAVAILABLE',
  // Чаты с заказчиком: сервер называет конкретику — чьи именно учётные записи не подошли, что
  // мешает удалить чат. Словарь фронта отвечает на те же коды общей фразой, и она уместна только
  // как запасная.
  'CUSTOMER_CHAT_MEMBER_INVALID',
  'CUSTOMER_CHAT_HAS_MESSAGES',
  'CUSTOMER_CHAT_NOT_SHARED',
]);

/** Shape of the backend `ErrorResponse` envelope, as far as message extraction cares. */
interface ApiErrorBody {
  code?: string;
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
  details?: { errors?: Record<string, string[]>; reason?: unknown };
}

/**
 * First per-field message of a model-validation response. The envelope nests them
 * under `details.errors`; top-level `errors` is kept as a legacy fallback.
 *
 * `russianOnly` skips messages the framework produced itself (JSON binding failures
 * carry raw English like «The JSON value could not be converted to…») — those are
 * worse for the user than the generic envelope text.
 */
function firstValidationError(
  data: ApiErrorBody | undefined,
  russianOnly = false
): string | undefined {
  const validationErrors = data?.details?.errors ?? data?.errors;
  if (!validationErrors) {
    return undefined;
  }
  return Object.values(validationErrors)
    .flat()
    .find((message) => !!message && (!russianOnly || isRussianMessage(message)));
}

/**
 * Extract error message from API error response
 * Returns user-friendly Russian error messages based on error codes, HTTP status, or system errors
 */
export function getErrorMessage(error: unknown): string {
  // Handle network error (no response from server)
  if (isNetworkError(error)) {
    return SYSTEM_ERROR_MESSAGES.NETWORK_ERROR;
  }

  // Handle timeout error
  if (isTimeoutError(error)) {
    return SYSTEM_ERROR_MESSAGES.TIMEOUT_ERROR;
  }

  if (isAxiosError(error)) {
    // Try to get data from response
    const data = error.response?.data as ApiErrorBody | undefined;

    // If an API message is already in Russian, use it. Covers both the standard
    // `message` envelope and inline `{ error: "..." }` controller returns.
    const apiMessage = data?.message ?? data?.error;

    // The model-validation envelope is generic by design («Проверьте правильность
    // заполнения полей.») — the useful text is the per-field message the backend
    // assembled under details.errors («Некорректный формат номера телефона»).
    // Checked before the code mapping below, which would otherwise short-circuit
    // on VALIDATION_ERROR and throw that detail away.
    if (data?.code === 'VALIDATION_ERROR') {
      const fieldError = firstValidationError(data, true);
      if (fieldError) {
        return fieldError;
      }
    }

    // Check for error code from backend and map to Russian message.
    // Exception — ENTITY_IN_USE: the backend names the exact blocker («услуга
    // используется в заявках»), which beats our generic «элемент используется
    // в других записях». Every other code keeps the curated frontend text.
    if (data?.code) {
      // A refusal refined by `details.reason` (COMPLETION_NOT_ALLOWED: SEVERAL_MECHANICS /
      // NOT_A_MECHANIC) reads its own text before the code's generic one.
      const reason = data.details?.reason;
      const reasonMessage =
        typeof reason === 'string' ? ERROR_REASON_MESSAGES[`${data.code}.${reason}`] : undefined;
      if (reasonMessage) {
        return reasonMessage;
      }

      const preferBackendMessage =
        PREFER_BACKEND_MESSAGE_CODES.has(data.code) &&
        !!apiMessage &&
        isRussianMessage(apiMessage);
      const mappedMessage = ERROR_MESSAGES[data.code];
      if (mappedMessage && !preferBackendMessage) {
        return mappedMessage;
      }
    }

    if (apiMessage && isRussianMessage(apiMessage)) {
      return apiMessage;
    }

    // First validation error for envelopes that carry no recognised code.
    const validationError = firstValidationError(data);
    if (validationError) {
      return validationError;
    }

    // Fallback to HTTP status code Russian message
    const status = error.response?.status;
    if (status && HTTP_ERROR_MESSAGES[status]) {
      return HTTP_ERROR_MESSAGES[status];
    }
  }

  // Check if standard Error message is already in Russian
  if (error instanceof Error && isRussianMessage(error.message)) {
    return error.message;
  }

  // Default Russian error message
  return DEFAULT_ERROR_MESSAGE;
}

/**
 * Check if error is a network error (no response from server)
 */
export function isNetworkError(error: unknown): boolean {
  if (isAxiosError(error)) {
    return !error.response && error.code !== 'ECONNABORTED';
  }
  return false;
}

/**
 * Check if error is a timeout error
 */
export function isTimeoutError(error: unknown): boolean {
  if (isAxiosError(error)) {
    return error.code === 'ECONNABORTED';
  }
  return false;
}

/**
 * Check if error is an authentication error (401)
 */
export function isAuthError(error: unknown): boolean {
  if (isAxiosError(error)) {
    return error.response?.status === 401;
  }
  return false;
}

/**
 * Check if error is a forbidden error (403)
 */
export function isForbiddenError(error: unknown): boolean {
  if (isAxiosError(error)) {
    return error.response?.status === 403;
  }
  return false;
}

/**
 * Check if error is a not found error (404)
 */
export function isNotFoundError(error: unknown): boolean {
  if (isAxiosError(error)) {
    return error.response?.status === 404;
  }
  return false;
}

/**
 * Check if error is a validation error (400)
 */
export function isValidationError(error: unknown): boolean {
  if (isAxiosError(error)) {
    return error.response?.status === 400;
  }
  return false;
}

/**
 * Check if error is a server error (5xx)
 */
export function isServerError(error: unknown): boolean {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    return status !== undefined && status >= 500 && status < 600;
  }
  return false;
}

// ============================================================================
// Request Helpers
// ============================================================================

/**
 * Interface for request options with abort support
 */
export interface RequestOptions {
  signal?: AbortSignal;
}

/**
 * Create an AbortController for cancellable requests
 */
export function createAbortController(): AbortController {
  return new AbortController();
}

/**
 * Set a custom header for a single request
 */
export function setAuthHeader(config: InternalAxiosRequestConfig, token: string): void {
  config.headers.Authorization = `Bearer ${token}`;
}

// ============================================================================
// Re-exports
// ============================================================================

export { TokenStorage };
export type { AxiosError, AxiosInstance };
export default apiClient;
